import { Component, Input, Renderer2 } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Thesaurus } from '@interfaces/thesaurus.interface';
import { ThesaurusService } from '@thesaurus-service/thesaurus.service';
import { ThesaurusEntry } from '@interfaces/thesaurus-entry.interface';
import { TermsService } from '@terms-service/terms.service';
import { EntryService } from 'app/services/entry/entry.service';
import { DetailContext } from '@interfaces/detail-context.interface';
import { EventsService } from 'app/services/events/events.service';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
    selector: 'app-thesaurus-single-term',
    templateUrl: './thesaurus-single-term.component.html',
    styleUrl: './thesaurus-single-term.component.scss',
    standalone: false
})
export class ThesaurusSingleTermComponent {
  @Input() thesaurus?: Thesaurus;
  @Input() token!: string;
  @Input() thesauri?: Thesaurus[];
  @Input() thesaurusId: string = '';
  @Input() entryOrdinal: number = 0;
  @Input() detailCtx?: DetailContext;
  closePopup: boolean = true;
  appendToField: boolean = false;
  proxyRedirect: boolean = true;
  targetFieldName: string = '';
  entry!: ThesaurusEntry;
  rootNode!: ThesaurusEntry;
  useTermsInstead!: ThesaurusEntry;
  isUseForTerm: boolean = false;
  hasNotes: boolean = false;
  hasBroaderTerms: boolean = false;
  hasNarrowerTerms: boolean = false;
  subscription: any;
  display: boolean = true;
  
  constructor(
    private thesaurusService: ThesaurusService,
    public termsService: TermsService,
    private eventsService: EventsService,
    private route: ActivatedRoute,
    private renderer: Renderer2,
    private entryService: EntryService, 
    private spinnerService: NgxSpinnerService
  ) {
  }

  ngOnInit(): void {
    if (!this.thesaurus?.hierarchical && this.termsService.getIsRelationalView() === false) {
      this.termsService.setIsRelationalView(true);
      this.setRelationalViewTabIndex();
    }
    this.hidePaginationButtons();
    if(this.termsService.getIsRelationalView()) {
      this.showIndexSpinner();
    }
    this.getEntryObject(this.thesaurusId, this.entryOrdinal, this.token).then( () => {
      if(this.termsService.getIsRelationalView()) {
        this.hideIndexSpinner();
      }
    });
    this.eventsService.emitUpdateProductImageURL(this.route.snapshot.queryParams['amp;productImage'] || '');
  }

  ngAfterViewInit(): void {
    let relationalViewElement = document.getElementById('relationalViewButton');
    let hierarchicalViewElement = document.getElementById('hierarchicalViewButton');

    // Set initial tabindex for view toggle buttons
    if (this.termsService.getIsRelationalView()) {
      this.renderer.setAttribute(relationalViewElement!, 'tabindex', '-1');
      this.renderer.setAttribute(hierarchicalViewElement!, 'tabindex', '0');
    } else {
      this.renderer.setAttribute(hierarchicalViewElement!, 'tabindex', '-1');
      this.renderer.setAttribute(relationalViewElement!, 'tabindex', '0');
    }
  }

  onBackLinkClick(): void {
    this.entryService.emitBackLinkClickEvent();
  }

  isExplode(): boolean {
    if (!this.thesaurus) {
      return false;
    }
    if (this.thesaurus.explode) {
      let thesWithExplode: number = 0;
      if (Array.isArray(this.thesauri) && this.thesauri.length > 1) {
        for (let thes of this.thesauri) {
          if (thes.explode) {
            thesWithExplode++;
          }
        }
        // if more than one thesaurus has explode set to true, don't display the checkbox
        return thesWithExplode == 1 ? true : false;
      }
      else if (this.thesaurus) {
        return this.thesaurus.explode;
      }
    }
    return false;
  }

  isMajor(): boolean {
    return this.thesaurus ? this.thesaurus.major : false;
  }

  async getEntryObject(thesaurusId: string, entryOrdinal: number, token: string): Promise<void> {
    this.entry = await firstValueFrom(this.thesaurusService.getthesaurusSingleEntry(thesaurusId, entryOrdinal, token));
    this.hasNotes = this.checkIfEntryHasNotes(this.entry);
    this.hasBroaderTerms = (this.entry.broaderTerms ?? []).length > 0;
    this.hasNarrowerTerms = (this.entry.narrowerTerms ?? []).length > 0;
    if (this.entry.useTermsInstead) {
      this.isUseForTerm = true;
      this.useTermsInstead = await firstValueFrom(this.thesaurusService.getthesaurusSingleEntry(thesaurusId, this.entry.useTermsInstead[0].termOrdinal, token));
      this.hasBroaderTerms = (this.useTermsInstead.broaderTerms ?? []).length > 0;
      this.hasNarrowerTerms = (this.useTermsInstead.narrowerTerms ?? []).length > 0;
      this.hasNotes = this.hasNotes || this.checkIfEntryHasNotes(this.useTermsInstead);
    }
    if (this.hasBroaderTerms) {
      this.entry.broaderTerms = await this.populateBroaderterms(this.useTermsInstead ? this.useTermsInstead : this.entry, thesaurusId, token);
    }
    if (this.hasNarrowerTerms) {
      this.entry.narrowerTerms = await this.populateNarrowerterms(this.useTermsInstead? this.useTermsInstead: this.entry, thesaurusId, token);
    }
  }

  checkIfEntryHasNotes(entry: ThesaurusEntry): boolean {
    return !!entry.description || !!entry.useTermFor?.length || !!entry.relatedTerms?.length;
  }

  async populateBroaderterms(entry: ThesaurusEntry, thesaurusId: string, token: string): Promise<ThesaurusEntry[]> {
    let entryBT : ThesaurusEntry[] = [];
    if (entry.broaderTerms) {
      if (await this.containsOnlyRootNode(entry.broaderTerms)) {
        this.hasBroaderTerms = false;
        return entryBT;
      }
      for (let term of entry.broaderTerms) {
        await firstValueFrom(this.thesaurusService.getthesaurusSingleEntry(thesaurusId, term.termOrdinal, token)).then(
            broaderTerm => {
            entryBT.push(broaderTerm);
            this.hasNotes = this.hasNotes || this.checkIfEntryHasNotes(broaderTerm);
          }
        );
      }
    }
    this.hasBroaderTerms = entryBT.length > 0;
    return entryBT;
  }

  async containsOnlyRootNode(broaderTerms: ThesaurusEntry[]): Promise<boolean> {
    if (this.thesaurusService.isThesaurusWithRoot(this.thesaurusId) === false) {
      return false;
    }
    this.rootNode = await firstValueFrom(this.thesaurusService.getthesaurusRoot(this.thesaurusId, this.token));
    return broaderTerms.length === 1 && broaderTerms[0].termOrdinal === this.rootNode.termOrdinal;
  }

  async populateNarrowerterms(entry: ThesaurusEntry, thesaurusId: string, token: string): Promise<ThesaurusEntry[]> {
    let entryNT : ThesaurusEntry[] = [];
    if (entry.narrowerTerms) {
      for (let term of entry.narrowerTerms) {
        await firstValueFrom(this.thesaurusService.getthesaurusSingleEntry(thesaurusId, term.termOrdinal, token)).then(
          narrowerTerm => {
            if (entryNT.findIndex(t => t.termOrdinal === narrowerTerm.termOrdinal) !== -1) {
              return;
            }
            entryNT.push(narrowerTerm);
            this.hasNotes = this.hasNotes || this.checkIfEntryHasNotes(narrowerTerm);
          }
        );
      }
    }
    this.hasNarrowerTerms = entryNT.length > 0;
    return entryNT;
  }

  setRelationalView(): void {
    this.termsService.setIsRelationalView(true);
    this.setRelationalViewTabIndex();
  }

  setHierarchicalView(): void {
    this.termsService.setIsRelationalView(false);
    this.setHierarchicalViewTabIndex();
  }

  setHierarchicalViewTabIndex(): void {

    let relationalViewElement = document.getElementById('relationalViewButton');
    let hierarchicalViewElement = document.getElementById('hierarchicalViewButton');

    this.renderer.setAttribute(hierarchicalViewElement!, 'tabindex', '-1');
    this.renderer.setAttribute(relationalViewElement!, 'tabindex', '0');
  }

  setRelationalViewTabIndex(): void {

    let relationalViewElement = document.getElementById('relationalViewButton');
    let hierarchicalViewElement = document.getElementById('hierarchicalViewButton');

    if(hierarchicalViewElement != null) {
      this.renderer.setAttribute(hierarchicalViewElement!, 'tabindex', '0');
    }
    if(relationalViewElement != null) {
      this.renderer.setAttribute(relationalViewElement!, 'tabindex', '-1');
    }
  }

  hidePaginationButtons() {
    let el = document.querySelector('.pagination-wrapper');
    el?.classList.add('hidden');
  }

  showIndexSpinner() {
    this.eventsService.emitShowIndexSpinner();
    this.eventsService.emitDisableHelpButtonEvent(true);
  }

  hideIndexSpinner() {
    this.eventsService.emitHideIndexSpinner();
    this.eventsService.emitDisableHelpButtonEvent(false);
  }

}
